import React, { useState, useEffect } from 'react';
import API from '../api/api';

function CreateLoadModal({ isOpen, onClose, onLoadCreated }) {
  const [formData, setFormData] = useState({
    weight: '',
    equipment_type: 'UNKNOWN',
    rate: '',
    pickup_date: '',
    pickup_time: '',
    delivery_date: '',
    delivery_time: '',
    origin_name: '',
    origin_address: '',
    destination_name: '',
    destination_address: '',
    special_instructions: '',
    driver_name: '',
    driver_phone: '',
    trailer_number: '',
    truck_number: '',
    shipper: '',
    carrier: '',
    organization: '' // For admin users to select target organization
  });

  // Multi-stop state
  const [stops, setStops] = useState([
    { sequence: 1, stop_type: 'CL', name: '', address: '', city: '', state: '', zip_code: '', appointment_date: '', appointment_time: '' },
    { sequence: 2, stop_type: 'CU', name: '', address: '', city: '', state: '', zip_code: '', appointment_date: '', appointment_time: '' }
  ]);
  const [useMultiStop, setUseMultiStop] = useState(false);

  const [costs, setCosts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [tradingPartners, setTradingPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
      fetchTradingPartners();
      fetchCurrentUser();
    }
  }, [isOpen]);

  const fetchOrganizations = async () => {
    try {
      const response = await API.get('organizations/');
      // Handle paginated response - extract results array
      const orgsData = response.data.results || response.data;
      setOrganizations(Array.isArray(orgsData) ? orgsData : []);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const fetchTradingPartners = async () => {
    try {
      const response = await API.get('trading-partners/');
      const partnersData = response.data.results || response.data;
      setTradingPartners(Array.isArray(partnersData) ? partnersData : []);
    } catch (err) {
      console.error('Error fetching trading partners:', err);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await API.get('auth/user/');
      setIsAdmin(response.data.organization_name === 'Admin');
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Stop management functions
  const handleStopChange = (index, field, value) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], [field]: value };
    setStops(newStops);
  };

  const addStop = () => {
    const newSequence = stops.length + 1;
    // Insert before the last delivery stop
    const newStops = [...stops];
    const lastStop = newStops.pop();
    newStops.push({
      sequence: newSequence - 1,
      stop_type: 'CU', // Default to delivery
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      appointment_date: '',
      appointment_time: ''
    });
    lastStop.sequence = newSequence;
    newStops.push(lastStop);
    
    // Re-sequence all stops
    newStops.forEach((stop, idx) => {
      stop.sequence = idx + 1;
    });
    
    setStops(newStops);
  };

  const removeStop = (index) => {
    if (stops.length <= 2) {
      alert('A load must have at least 2 stops (pickup and delivery)');
      return;
    }
    const newStops = stops.filter((_, i) => i !== index);
    // Re-sequence remaining stops
    newStops.forEach((stop, idx) => {
      stop.sequence = idx + 1;
    });
    setStops(newStops);
  };

  const addCost = () => {
    setCosts(prev => [...prev, {
      cost_type: 'miscellaneous',
      description: '',
      amount: '',
      date_incurred: '',
      location: '',
      notes: ''
    }]);
  };

  const removeCost = (index) => {
    setCosts(prev => prev.filter((_, i) => i !== index));
  };

  const handleCostChange = (index, field, value) => {
    const newCosts = [...costs];
    newCosts[index] = {
      ...newCosts[index],
      [field]: value
    };
    setCosts(newCosts);
  };

  const validateForm = () => {
    const errors = [];
    
    // Validate required main fields
    if (!formData.weight) errors.push('Weight is required');
    if (!formData.shipper) errors.push('Shipper is required');

    if (useMultiStop) {
      // Validate stops
      if (stops.length < 2) {
        errors.push('A load must have at least 2 stops');
      }
      stops.forEach((stop, index) => {
        if (!stop.name) errors.push(`Stop ${index + 1}: Name is required`);
        if (!stop.appointment_date) errors.push(`Stop ${index + 1}: Date is required`);
      });
      // Get first and last stop dates for pickup/delivery
      const firstStop = stops[0];
      const lastStop = stops[stops.length - 1];
      if (!firstStop.appointment_date) errors.push('Pickup date is required');
      if (!lastStop.appointment_date) errors.push('Delivery date is required');
    } else {
      // Original validation for simple mode
      if (!formData.pickup_date) errors.push('Pickup date is required');
      if (!formData.delivery_date) errors.push('Delivery date is required');
      if (!formData.origin_name) errors.push('Origin name is required');
      if (!formData.origin_address) errors.push('Origin address is required');
      if (!formData.destination_name) errors.push('Destination name is required');
      if (!formData.destination_address) errors.push('Destination address is required');
    }
    
    // Validate costs (if any are provided)
    const invalidCosts = costs.filter(cost => 
      (cost.description || cost.amount || cost.date_incurred) && 
      (!cost.description || !cost.amount || !cost.date_incurred)
    );
    if (invalidCosts.length > 0) {
      errors.push('All cost entries must have description, amount, and date incurred');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form before submission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      setLoading(false);
      return;
    }

    try {
      // Format dates to ensure YYYY-MM-DD format
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
      };

      let loadData;

      if (useMultiStop) {
        // Multi-stop mode: use stops data
        const firstStop = stops[0];
        const lastStop = stops[stops.length - 1];
        
        loadData = {
          ...formData,
          pickup_date: formatDate(firstStop.appointment_date),
          delivery_date: formatDate(lastStop.appointment_date),
          pickup_time: firstStop.appointment_time || null,
          delivery_time: lastStop.appointment_time || null,
          origin_name: firstStop.name,
          origin_address: [firstStop.address, firstStop.city, firstStop.state, firstStop.zip_code].filter(Boolean).join(', '),
          destination_name: lastStop.name,
          destination_address: [lastStop.address, lastStop.city, lastStop.state, lastStop.zip_code].filter(Boolean).join(', '),
          driver_name: formData.driver_name || null,
          driver_phone: formData.driver_phone || null,
          trailer_number: formData.trailer_number || null,
          truck_number: formData.truck_number || null,
          special_instructions: formData.special_instructions || null,
          // Include stops data for backend
          stops_data: stops.map(stop => ({
            sequence: stop.sequence,
            stop_type: stop.stop_type,
            name: stop.name,
            address: stop.address,
            city: stop.city,
            state: stop.state,
            zip_code: stop.zip_code,
            appointment_date: formatDate(stop.appointment_date),
            appointment_time: stop.appointment_time || null
          })),
          costs_data: costs
            .filter(cost => cost.description && cost.amount && cost.date_incurred)
            .map(cost => ({
              ...cost,
              date_incurred: formatDate(cost.date_incurred),
              location: cost.location || null,
              notes: cost.notes || ''
            }))
        };
      } else {
        // Simple mode: use form data directly
        loadData = {
          ...formData,
          pickup_date: formatDate(formData.pickup_date),
          delivery_date: formatDate(formData.delivery_date),
          pickup_time: formData.pickup_time || null,
          delivery_time: formData.delivery_time || null,
          driver_name: formData.driver_name || null,
          driver_phone: formData.driver_phone || null,
          trailer_number: formData.trailer_number || null,
          truck_number: formData.truck_number || null,
          special_instructions: formData.special_instructions || null,
          // Create default stops from origin/destination
          stops_data: [
            {
              sequence: 1,
              stop_type: 'CL',
              name: formData.origin_name,
              address: formData.origin_address,
              appointment_date: formatDate(formData.pickup_date),
              appointment_time: formData.pickup_time || null
            },
            {
              sequence: 2,
              stop_type: 'CU',
              name: formData.destination_name,
              address: formData.destination_address,
              appointment_date: formatDate(formData.delivery_date),
              appointment_time: formData.delivery_time || null
            }
          ],
          costs_data: costs
            .filter(cost => cost.description && cost.amount && cost.date_incurred)
            .map(cost => ({
              ...cost,
              date_incurred: formatDate(cost.date_incurred),
              location: cost.location || null,
              notes: cost.notes || ''
            }))
        };
      }

      console.log('Creating load with data:', loadData);
      
      const response = await API.post('loads/', loadData);
      
      console.log('Load creation response:', response);
      
      if (response.data) {
        console.log('Calling onLoadCreated with:', response.data);
        onLoadCreated(response.data);
        onClose();
        // Reset form
        setFormData({
          weight: '',
          equipment_type: 'UNKNOWN',
          rate: '',
          pickup_date: '',
          pickup_time: '',
          delivery_date: '',
          delivery_time: '',
          origin_name: '',
          origin_address: '',
          destination_name: '',
          destination_address: '',
          special_instructions: '',
          driver_name: '',
          driver_phone: '',
          trailer_number: '',
          truck_number: '',
          shipper: '',
          carrier: '',
          organization: ''
        });
        setStops([
          { sequence: 1, stop_type: 'CL', name: '', address: '', city: '', state: '', zip_code: '', appointment_date: '', appointment_time: '' },
          { sequence: 2, stop_type: 'CU', name: '', address: '', city: '', state: '', zip_code: '', appointment_date: '', appointment_time: '' }
        ]);
        setCosts([]);
        setUseMultiStop(false);
      }
    } catch (err) {
      console.error('Error creating load:', err);
      console.error('Error response:', err.response);
      
      // Handle different types of errors
      let errorMessage = 'Failed to create load';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else {
          // Handle field-specific errors
          const errors = [];
          Object.keys(err.response.data).forEach(field => {
            if (Array.isArray(err.response.data[field])) {
              errors.push(`${field}: ${err.response.data[field].join(', ')}`);
            } else {
              errors.push(`${field}: ${err.response.data[field]}`);
            }
          });
          errorMessage = errors.length > 0 ? errors.join('\n') : 'Validation error occurred';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Create New Load</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-700">
                {error.split('\n').map((line, index) => (
                  <p key={index} className="mb-1 last:mb-0">{line}</p>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Load Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Load Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shipper *
                  </label>
                  <select
                    name="shipper"
                    value={formData.shipper}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select Shipper</option>
                    {tradingPartners.map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.name || partner.edi_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carrier
                  </label>
                  <select
                    name="carrier"
                    value={formData.carrier}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select Carrier</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                {/* Organization selector for admin users */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Organization
                    </label>
                    <select
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Organization (optional)</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use your admin organization
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (lbs) *
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipment Type
                  </label>
                  <select
                    name="equipment_type"
                    value={formData.equipment_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="UNKNOWN">Unknown</option>
                    <option value="DRY">Dry Van</option>
                    <option value="REEFER">Reefer</option>
                    <option value="FLATBED">Flatbed</option>
                    <option value="POWER_ONLY">Power Only</option>
                    <option value="STEP_DECK">Step Deck</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Name
                  </label>
                  <input
                    type="text"
                    name="driver_name"
                    value={formData.driver_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Phone
                  </label>
                  <input
                    type="tel"
                    name="driver_phone"
                    value={formData.driver_phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trailer Number
                  </label>
                  <input
                    type="text"
                    name="trailer_number"
                    value={formData.trailer_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Truck Number
                  </label>
                  <input
                    type="text"
                    name="truck_number"
                    value={formData.truck_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Instructions
                  </label>
                  <textarea
                    name="special_instructions"
                    value={formData.special_instructions}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Multi-Stop Toggle */}
            <div className="bg-primary-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-gray-900">Multi-Stop Load</h4>
                  <p className="text-sm text-gray-600">Enable to add more than 2 stops (pickup/delivery)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useMultiStop}
                    onChange={(e) => setUseMultiStop(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>

            {useMultiStop ? (
              /* Multi-Stop Mode */
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Stops ({stops.length})</h4>
                  <button
                    type="button"
                    onClick={addStop}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                  >
                    + Add Stop
                  </button>
                </div>
                
                <div className="space-y-4">
                  {stops.map((stop, index) => (
                    <div key={index} className={`p-4 border rounded-lg ${
                      ['CL', 'PU', 'PL', 'LD'].includes(stop.stop_type)
                        ? 'bg-green-50 border-green-200'
                        : 'bg-primary-50 border-primary-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white text-gray-900 font-semibold text-sm border">
                            {index + 1}
                          </span>
                          <select
                            value={stop.stop_type}
                            onChange={(e) => handleStopChange(index, 'stop_type', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="CL">Pickup (Complete Load)</option>
                            <option value="PU">Pickup</option>
                            <option value="PL">Pickup (Part Load)</option>
                            <option value="CU">Delivery (Complete Unload)</option>
                            <option value="DL">Delivery</option>
                          </select>
                        </div>
                        {stops.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeStop(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="lg:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Location Name *</label>
                          <input
                            type="text"
                            value={stop.name}
                            onChange={(e) => handleStopChange(index, 'name', e.target.value)}
                            placeholder="Warehouse name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                          <input
                            type="date"
                            value={stop.appointment_date}
                            onChange={(e) => handleStopChange(index, 'appointment_date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Time</label>
                          <input
                            type="time"
                            value={stop.appointment_time}
                            onChange={(e) => handleStopChange(index, 'appointment_time', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Street Address</label>
                          <input
                            type="text"
                            value={stop.address}
                            onChange={(e) => handleStopChange(index, 'address', e.target.value)}
                            placeholder="123 Main St"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                          <input
                            type="text"
                            value={stop.city}
                            onChange={(e) => handleStopChange(index, 'city', e.target.value)}
                            placeholder="City"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              value={stop.state}
                              onChange={(e) => handleStopChange(index, 'state', e.target.value)}
                              placeholder="ST"
                              maxLength={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">ZIP</label>
                            <input
                              type="text"
                              value={stop.zip_code}
                              onChange={(e) => handleStopChange(index, 'zip_code', e.target.value)}
                              placeholder="12345"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Simple Mode - Pickup and Delivery */
              <>
                {/* Pickup Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Pickup Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Location Name *
                      </label>
                      <input
                        type="text"
                        name="origin_name"
                        value={formData.origin_name}
                        onChange={handleInputChange}
                        required={!useMultiStop}
                        placeholder="e.g., ABC Warehouse"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Date *
                      </label>
                      <input
                        type="date"
                        name="pickup_date"
                        value={formData.pickup_date}
                        onChange={handleInputChange}
                        required={!useMultiStop}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Time
                      </label>
                      <input
                        type="time"
                        name="pickup_time"
                        value={formData.pickup_time}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Address *
                      </label>
                      <textarea
                        name="origin_address"
                        value={formData.origin_address}
                        onChange={handleInputChange}
                        required={!useMultiStop}
                        rows="2"
                        placeholder="123 Main St, City, ST 12345"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Delivery Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Location Name *
                      </label>
                      <input
                        type="text"
                        name="destination_name"
                        value={formData.destination_name}
                        onChange={handleInputChange}
                        required={!useMultiStop}
                        placeholder="e.g., XYZ Distribution Center"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Date *
                      </label>
                      <input
                        type="date"
                        name="delivery_date"
                        value={formData.delivery_date}
                        onChange={handleInputChange}
                        required={!useMultiStop}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Time
                      </label>
                      <input
                        type="time"
                        name="delivery_time"
                        value={formData.delivery_time}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Address *
                      </label>
                      <textarea
                        name="destination_address"
                        value={formData.destination_address}
                        onChange={handleInputChange}
                        required={!useMultiStop}
                        rows="2"
                        placeholder="456 Oak Ave, City, ST 67890"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Costs */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Costs</h4>
                <button
                  type="button"
                  onClick={addCost}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Add Cost
                </button>
              </div>
              
              {costs.map((cost, index) => (
                <div key={index} className="mb-4 p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-md font-medium text-gray-900">Cost #{index + 1}</h5>
                    <button
                      type="button"
                      onClick={() => removeCost(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cost Type
                      </label>
                      <select
                        value={cost.cost_type}
                        onChange={(e) => handleCostChange(index, 'cost_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="fuel">Fuel</option>
                        <option value="lumper">Lumper</option>
                        <option value="toll">Toll</option>
                        <option value="detention">Detention</option>
                        <option value="layover">Layover</option>
                        <option value="driver_pay">Driver Pay</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="insurance">Insurance</option>
                        <option value="permit">Permit</option>
                        <option value="scale">Scale</option>
                        <option value="miscellaneous">Miscellaneous</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={cost.description}
                        onChange={(e) => handleCostChange(index, 'description', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount ($) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={cost.amount}
                        onChange={(e) => handleCostChange(index, 'amount', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date Incurred *
                      </label>
                      <input
                        type="date"
                        value={cost.date_incurred}
                        onChange={(e) => handleCostChange(index, 'date_incurred', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <input
                        type="text"
                        value={cost.location}
                        onChange={(e) => handleCostChange(index, 'location', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={cost.notes}
                        onChange={(e) => handleCostChange(index, 'notes', e.target.value)}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Load'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateLoadModal;
